'use client';
import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ProfileAvatar } from '@/components/Profile/ProfileAvatar';
import { usePathname, useSearchParams } from 'next/navigation';
import { HeaderType } from '@/types/headerSchemas';
import { SearchBarNew } from '../search/search-bar-new';
import { AskAiButton } from '../Ask-ai/AskAiButton';
import { useAskAi } from '../Ask-ai/AskAiProvider';
import MaterialIcons from '../MaterialIcons/materialIcons';
import { useTheme } from '../Theme/theme-provider';
import { Button } from '../ui/button';
import { useLoginModals } from '../Login/common/LoginModalsManager';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { buildAbsoluteRedirectUrl } from '@/utils/auth/common/auth-storage';
import { ThemeToggle } from '../Theme/theme-toggle';
import { useLogoutConfirmStore } from '@/store/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ProductSlideMenu } from './ProductSlideMenu';
import { getProducts } from '@/lib/actions/get-products';
import { PROFILE_SECTION_NAV_ITEMS } from '@/components/Profile/ProfileSidebar';
import { AskAiHeaderButton, AskAiContext } from '@/components/Ask-ai/ask-ai';
import { useSearchChatCoordination } from '@/utils/search-chat-coordination';

interface UtilityNavData {
  upper_right_menus?: Array<{
    menu_link: {
      title: string;
      href?: string;
    };
    show_this_button?: boolean;
    open_in_new_tab?: boolean;
    _metadata?: {
      uid?: string;
    };
  }>;
}

interface NavigationLink {
  label: string;
  href: string;
  requiresAuth?: boolean;
  openInNewTab?: boolean;
}

interface MainNavigationProps {
  headerData: HeaderType;
  utilityNavData?: UtilityNavData;
  /** Server-resolved login state from cookie; avoids header flicker after login */
  initialIsLoggedIn?: boolean;
}

// Hardcoded navigation links - easy to maintain and reuse
const NAVIGATION_LINKS: NavigationLink[] = [
  { label: 'Catalog', href: '/catalog' },
  { label: 'Learning Paths', href: '/learning-paths' },
  { label: 'Training Instance', href: '/training-instance' },
];

export function MainNavigation({
  headerData,
  utilityNavData,
  initialIsLoggedIn = false,
}: MainNavigationProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Coordination for search-chat behavior
  const coordination = useSearchChatCoordination();
  const askAiCtx = useContext(AskAiContext);
  const isAskAiOpen = askAiCtx?.isOpen ?? false;
  const [profilePictureCacheBust, setProfilePictureCacheBust] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showProductMenu, setShowProductMenu] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const savedScrollYRef = useRef<number>(0);
  const { resolvedTheme, theme, setTheme } = useTheme();

  // Canonical auth state (see lib/auth/common/useSession.ts) — single reactive source,
  // replaces the old isUserLoggedIn() reads + 1s poll + storage/event effects.
  const { user, isLoggedIn: authLoggedIn, isLoading: authLoading } = useAuthUser();
  // Shared with AnnouncementBar (see store/store.ts useLogoutConfirmStore) — one modal
  // instance app-wide, mounted once in app/layout.tsx, instead of a separate copy here.
  const openLogoutConfirm = useLogoutConfirmStore((s) => s.open);

  // Get login modals from context
  let loginModals: { openLoginModal: () => void; closeLoginModal: () => void } | null = null;
  try {
    loginModals = useLoginModals();
  } catch {
    // Context not available, will handle gracefully
  }

  // Get Ask AI context - optional, will work without it
  let askAiContext: { openChat: () => void; closeChat: () => void; isOpen: boolean } | null = null;
  try {
    askAiContext = useAskAi();
  } catch {
    // Context not available, will handle gracefully
  }

  // Derived, not synced-via-effect: initials/picture/login-state are pure functions of
  // the auth hook's primitive fields. Depending on `user` (a whole object) here would be
  // wrong even inside useMemo — useAuthUser() hands back a new object reference on every
  // refetch (window focus, the 60s staleTime expiry, any userDataUpdated-triggered
  // invalidation) even when the actual data hasn't changed, since localUser() reparses
  // JSON from localStorage each call. Keying on the primitives instead means this only
  // recomputes when a value that actually matters changes — not on every unrelated
  // refetch — which also used to force-bump profilePictureCacheBust (see below) and
  // reload the avatar image on every window focus.
  const isLoggedIn = authLoading ? initialIsLoggedIn : authLoggedIn;

  const userInitials = useMemo(() => {
    if (authLoading) return ''; // keep the server-seeded blank state until resolved
    if (!authLoggedIn) return '';
    const firstName = (user?.firstName as string) || '';
    const lastName = (user?.lastName as string) || '';
    const initials = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`.trim();
    return initials || 'U'; // Fallback to 'U' if no initials
  }, [authLoading, authLoggedIn, user?.firstName, user?.lastName]);

  const profilePicture = useMemo(() => {
    if (authLoading || !authLoggedIn) return null;
    const picture = user?.profilePicture as string | undefined;
    return picture && picture.trim() !== '' ? picture : null;
  }, [authLoading, authLoggedIn, user?.profilePicture]);

  // The one genuine side effect here: force the <img> to actually reload after the
  // picture URL changes (e.g. a fresh upload can reuse the same URL, which the browser
  // would otherwise serve from cache). Bumped only when the URL itself changes, not on
  // every unrelated re-render/refetch.
  const prevProfilePictureRef = useRef(profilePicture);
  useEffect(() => {
    if (prevProfilePictureRef.current !== profilePicture) {
      prevProfilePictureRef.current = profilePicture;
      setProfilePictureCacheBust(Date.now());
    }
  }, [profilePicture]);

  // Redirect off a protected route once we know the user is logged out.
  useEffect(() => {
    if (authLoading || authLoggedIn) return;
    if (pathname.startsWith('/profile')) {
      window.location.href = buildAbsoluteRedirectUrl('/');
    }
  }, [authLoggedIn, authLoading, pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowProductMenu(false);
    setProducts([]);
  }, [pathname]);

  // Fetch products when product menu opens - always fetch fresh data every time
  useEffect(() => {
    if (showProductMenu) {
      setIsLoadingProducts(true);
      getProducts()
        .then((data) => {
          const productsData = data.products || [];
          setProducts(productsData);
          setIsLoadingProducts(false);
        })
        .catch((error) => {
          setIsLoadingProducts(false);
          setProducts([]); // Clear products on error
        });
    }
  }, [showProductMenu]);


  // Calculate header height (main navigation only)
  useEffect(() => {
    const calculateHeaderHeight = () => {
      if (typeof window !== 'undefined' && headerRef.current) {
        const navElement = headerRef.current;
        const navRect = navElement.getBoundingClientRect();
        let totalHeight = navRect.height;
        
        // Add search bar height if open
        if (isSearchOpen) {
          const searchBar = navElement.nextElementSibling as HTMLElement;
          if (searchBar && searchBar.classList.contains('border-t')) {
            totalHeight += searchBar.getBoundingClientRect().height;
          }
        }
        
        setHeaderHeight(Math.max(totalHeight, 56));
      }
    };

    calculateHeaderHeight();
    
    if (isMobileMenuOpen || isSearchOpen) {
      const timeoutId = setTimeout(calculateHeaderHeight, 50);
      const timeoutId2 = setTimeout(calculateHeaderHeight, 200);
      
      window.addEventListener('resize', calculateHeaderHeight);
      
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(timeoutId2);
        window.removeEventListener('resize', calculateHeaderHeight);
      };
    }
    
    window.addEventListener('resize', calculateHeaderHeight);
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
    };
  }, [isSearchOpen, isMobileMenuOpen]);

  // Lock body scroll when menu is open; restore normal scroll when menu closes
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      savedScrollYRef.current = scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Hide announcement bar only (keep header visible)
      const announcementBars = document.querySelectorAll('.bg-custom-nav-gradient');
      announcementBars.forEach((bar) => {
        (bar as HTMLElement).style.display = 'none';
      });
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        // Restore scroll on next frame so layout has reflowed after removing position:fixed
        const y = savedScrollYRef.current;
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
        
        // Show announcement bar again
        announcementBars.forEach((bar) => {
          (bar as HTMLElement).style.display = '';
        });
      };
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    const willClose = isMobileMenuOpen;
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close search when opening mobile menu
    if (!willClose) {
      setIsSearchOpen(false);
    }
    // Close product menu and reset when closing mobile menu
    if (willClose) {
      setShowProductMenu(false);
      setProducts([]);
    }
  };
  const toggleSearch = () => {
    const willOpen = !isSearchOpen;
    setIsSearchOpen(!isSearchOpen);
    
    // Notify coordination system
    if (willOpen) {
      coordination.onSearchOpened();
    } else {
      coordination.onSearchClosed();
    }
    
    // Close mobile menu when opening search
    if (willOpen) {
      setIsMobileMenuOpen(false);
      setShowProductMenu(false);
      setProducts([]);
    }
  };

  const isLoginLink = (link: { menu_link?: { href?: string; title?: string } }): boolean => {
    if (!link?.menu_link) return false;
    const title = link.menu_link.title?.toLowerCase() || '';
    const href = link.menu_link.href?.toLowerCase() || '';
    return title.includes('login') || href.includes('login');
  };

  /** When clicking Catalog from catalog page, reset entire page to default: full reload so checkboxes and data clear */
  const handleCatalogNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: NavigationLink) => {
    if (link.href !== '/catalog') return;
    const isOnCatalog = pathname === '/catalog' || pathname.endsWith('/catalog');
    const hasAnyParams = searchParams.toString().length > 0;
    if (isOnCatalog && hasAnyParams) {
      e.preventDefault();
      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';
      window.location.href = `${basePath}/catalog`;
      setIsMobileMenuOpen(false);
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, link: any) => {
    if (isLoginLink(link)) {
      e.preventDefault();
      loginModals?.openLoginModal();
      setIsMobileMenuOpen(false);
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogoutClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    openLogoutConfirm();
    setIsMobileMenuOpen(false);
  };

  // Transform link data: replace Academy with Contentstack.com
  const transformLinkData = (link: any) => {
    const originalTitle = link?.menu_link?.title || '';
    const isAcademyLink = originalTitle.toLowerCase().includes('academy');
    
    if (isAcademyLink) {
      return {
        ...link,
        menu_link: {
          ...link.menu_link,
          title: originalTitle.replace(/Academy/gi, 'Contentstack.com'),
          href: 'https://www.contentstack.com/',
        },
        open_in_new_tab: true,
        isContentstackLink: true,
      };
    }
    
    return link;
  };

  // Filter and prepare utility menu links
  const utilityLinks = useMemo(() => {
    if (!utilityNavData?.upper_right_menus) return [];
    
    return utilityNavData.upper_right_menus.map((link: any) => {
      // Transform Academy links to Contentstack.com
      let transformedLink = transformLinkData(link);
      
      // If logged in and this is a login link, transform it to logout
      if (isLoginLink(transformedLink) && isLoggedIn) {
        transformedLink = {
          ...transformedLink,
          menu_link: {
            ...transformedLink.menu_link,
            title: 'Logout',
          },
          isLogout: true,
        };
      }
      
      return transformedLink;
    }).filter((link: any) => {
      if (!link?.show_this_button) return false;
      return true;
    });
  }, [utilityNavData?.upper_right_menus, isLoggedIn]);

  const getLogo = () => {
    const brandingElements = headerData?.main_header_branding_elements;
    if (!brandingElements) return null;
    const darkLogo = (brandingElements as any)?.dark_mode_logo
    const lightLogo = (brandingElements as any)?.light_mode_logo
    
    if (resolvedTheme == 'dark' && darkLogo?.url) {
      return darkLogo;
    }
    if (resolvedTheme == 'light' && lightLogo?.url) {
      return lightLogo;
    }
    return brandingElements?.site_logo;
  };

  const logo = getLogo();

  return (
    <>
      {/* Desktop Navigation */}
      <nav
        className={`sticky top-0 z-50 mx-auto w-full items-center justify-center bg-surface-1 text-text-default-light border-b border-border-light ${isAskAiOpen ? 'hidden' : 'hidden min-[1500px]:flex'}`}
        data-test-id="header-component"
        data-academy-navbar
        data-swiftype-index="false"
        suppressHydrationWarning
      >
        <div className="section py-4 grid w-full grid-cols-[auto_1fr_auto] items-center gap-2 min-[1500px]:gap-3 xl:gap-4">
          {/* Left: Logo */}
          <div className="hidden items-center gap-2 min-[1500px]:flex flex-shrink-0 justify-start min-w-0">
            {logo?.url && (
              <Link
                href={'/'}
                className="flex items-center flex-shrink-0"
              >
                <Image
                  src={logo.url}
                  alt={logo.title || headerData?.main_header_branding_elements?.alternate_text_for_the_logo || 'Contentstack Academy Logo'}
                  width={180}
                  height={16}
                  unoptimized
                  className="h-4 w-auto sm:h-5 max-w-[180px] object-contain"
                  loading="eager"
                />
              </Link>
            )}
          </div>

          {/* Center: Search Bar and Ask AI */}
          <div className="hidden items-center justify-center px-2 min-[1500px]:px-3 xl:px-4 min-[1500px]:flex min-w-0 flex-1 overflow-visible">
            <div className="flex items-center gap-[10px]">
              <div className="max-w-[540px] min-w-[320px] flex-shrink-0">
                <SearchBarNew maxWidth="540px" />
              </div>
              <div className="flex-shrink-0">
                <AskAiHeaderButton />
              </div>
            </div>
          </div>

          {/* Right: Menu Links and Profile Icon */}
          <div className="hidden items-center gap-3 min-[1500px]:gap-4 xl:gap-6 min-[1500px]:flex flex-shrink-0 justify-end min-w-0 pl-2 min-[1500px]:pl-3 xl:pl-4">
            <ul className="flex items-center gap-3 min-[1500px]:gap-4 xl:gap-6">
              {NAVIGATION_LINKS.map((link, index) => {
                // Only show auth-required links if user is logged in
                if (link.requiresAuth && !isLoggedIn) return null;
                
                // Check if link is active
                const isActive = (() => {
                  if (link.href.includes('?')) {
                    // For links with query params
                    const [path, query] = link.href.split('?');
                    const tabParam = query.split('=')[1];
                    return pathname === path && searchParams.get('tab') === tabParam;
                  } else {
                    // For simple paths
                    return pathname === link.href || pathname.startsWith(link.href + '/');
                  }
                })();
                
                return (
                  <li key={`${link.label}-${index}`} className="whitespace-nowrap shrink-0">
                    <Link
                      href={link.href}
                      target={link.openInNewTab ? '_blank' : '_self'}
                      rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                      className={`cs-s-body-light whitespace-nowrap ${
                        isActive 
                          ? 'text-text-default-strong' 
                          : 'hover:text-text-default-strong'
                      }`}
                      data-test-id={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      onClick={(e) => handleCatalogNavClick(e, link)}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            
            {/* Profile Avatar - Desktop: dropdown with Learning dashboard & Logout */}
            {isLoggedIn && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center cursor-pointer justify-center transition-opacity hover:opacity-80 flex-shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Expand profile menu"
                  data-test-id="profile-avatar-desktop"
                >
                  <ProfileAvatar
                    profilePicture={profilePicture}
                    userInitials={userInitials}
                    cacheBust={profilePictureCacheBust}
                    size="sm"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[12rem] bg-surface-3 border-0 p-[8px] shadow-lg">
                  <DropdownMenuItem asChild>
                    <Link
                      href="/profile?tab=learning-dashboard"
                      className="cursor-pointer !p-2 cs-s-body text-text-default-medium hover:bg-surface-4 hover:text-text-default-strong focus:bg-surface-4"
                    >
                      Learning dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <button
                      type="button"
                      onClick={handleLogoutClick}
                      className="w-full cursor-pointer !p-2 cs-s-body text-text-default-medium hover:bg-surface-4 hover:text-text-default-strong focus:bg-surface-4 text-left"
                    >
                      Logout
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile/Tablet Navigation */}
      <div
        ref={headerRef}
        className={`sticky bg-surface-1 top-0 z-50 border-b border-border-light ${isAskAiOpen ? 'flex flex-col' : 'max-[1499px]:flex max-[1499px]:flex-col hidden'}`}
        data-academy-navbar
        data-swiftype-index="false"
        suppressHydrationWarning
      >
      <div className={`flex items-center justify-between py-4 gap-2 ${isAskAiOpen ? 'px-4 tab:px-8 min-[1200px]:px-[72px]' : 'px-4 tab:px-8'}`}>
        {/* Logo - Left side */}
        {logo?.url && (
          <Link
            href={'/'}
            className={`flex items-center transition-opacity hover:opacity-80 flex-shrink-0 z-10 ${isAskAiOpen ? '' : 'sm:ml-2.5'}`}
          >
            <Image
              src={logo.url}
              loading="eager"
              width={180}
              height={16}
              alt={logo.title || headerData?.main_header_branding_elements?.alternate_text_for_the_logo || 'Logo'}
              unoptimized
              className="h-4 w-auto sm:h-5 max-w-[180px] min-w-[120px] object-contain"
            />
          </Link>
        )}

        {/* Tablet: search bar + Ask AI in header */}
        <div className="hidden flex-1 items-center justify-center px-2 tab:px-4 tab:flex max-[1499px]:tab:flex min-w-0 overflow-visible">
          <div className="flex items-center gap-[10px]">
            <div className="w-full max-w-[320px] flex-shrink-0">
              <SearchBarNew
                maxWidth="320px"
                isOpen={isSearchOpen}
                onClose={() => {
                  setIsSearchOpen(false);
                  coordination.onSearchClosed();
                }}
                onOpenRequest={() => {
                  setIsSearchOpen(true);
                  coordination.onSearchOpened();
                  setIsMobileMenuOpen(false);
                  setShowProductMenu(false);
                  setProducts([]);
                }}
              />
            </div>
            <div className="flex-shrink-0 hidden desktop:block">
              <AskAiHeaderButton />
            </div>
          </div>
        </div>

        {/* Right side: Search icon (mobile only), Hamburger, and Profile */}
        <div className="flex items-center justify-center gap-6 flex-shrink-0 z-10">
          {/* Search Icon - Mobile only: opens full-screen search */}
          <button
            onClick={toggleSearch}
            className="flex items-center justify-center text-text-default-light hover:text-text-default-strong tab:hidden transition-colors duration-200"
            aria-label="Toggle search"
          >
            <div className="relative w-6 h-6 flex items-center justify-center search-icon-container">
              <MaterialIcons
                iconName="search"
                className={`absolute inset-0 m-auto text-[24px] text-text-default-light transition-opacity duration-200 ${
                  isSearchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              />
              <MaterialIcons
                iconName="close"
                className={`absolute inset-0 m-auto text-[24px] leading-[24px] text-text-default-light transition-opacity duration-200 ${
                  isSearchOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              />
            </div>
          </button>
          {/* Hamburger Menu Icon */}
          <button
            className={`flex items-center justify-center transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-90' : 'rotate-0'}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <MaterialIcons
              iconName={isMobileMenuOpen ? 'close' : 'menu'}
              className="text-text-default-light"
            />
          </button>
        </div>
      </div>

      {/* Mobile + Tab: Full-screen search modal when search icon is clicked */}
      {isSearchOpen && (
        <SearchBarNew
          maxWidth="100%"
          isOpen={isSearchOpen}
          onClose={() => {
            setIsSearchOpen(false);
            coordination.onSearchClosed();
          }}
          autoFocus={isSearchOpen}
        />
      )}

      {/* Mobile Menu with smooth transitions */}
      <div
        className={`fixed z-[9998] transition-opacity duration-300 ease-out ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          top: `${headerHeight}px`,
          left: 0,
          right: isAskAiOpen ? 'min(420px, 70vw, calc(100vw - 280px))' : 0,
          bottom: 0,
        }}
      >
        {/* Backdrop - modal-style overlay (matches dialog overlay) */}
        <div
          className="absolute inset-0 bg-[var(--Surfaces-Overlay-Sheen-75)] backdrop-blur-[8px] transition-opacity duration-300 ease-out"
          onClick={() => {
            setIsMobileMenuOpen(false);
            setShowProductMenu(false);
            setProducts([]);
          }}
          aria-hidden="true"
        />
        {/* Menu panel: full screen on mobile, right 50% on tablet; overflow-y-auto for normal scroll inside menu */}
        <div
          className={`absolute inset-0 tab:left-1/2 tab:right-0 flex flex-col overflow-y-auto overscroll-contain scroll-touch bg-surface-2 text-text-default-strong transition-all duration-200 ease-in-out ${
            isMobileMenuOpen ? 'opacity-100 translate-y-0 tab:translate-x-0' : 'opacity-0 -translate-y-1 tab:translate-y-0 tab:translate-x-full'
          }`}
        >
            <div className="flex flex-col w-full bg-surface-2">
              {/* Logged-in User Section - Mobile/Tablet only */}
              {isLoggedIn ? (
                <div className="px-4 py-4 bg-surface-3 w-full flex items-center justify-between">
                  {/* Profile Icon - Left */}
                  <Link
                    href="/profile"
                    className="flex items-center justify-center transition-opacity hover:opacity-80 flex-shrink-0"
                    aria-label="Profile"
                    data-test-id="profile-avatar"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <ProfileAvatar
                      profilePicture={profilePicture}
                      userInitials={userInitials}
                      cacheBust={profilePictureCacheBust}
                      size="sm"
                    />
                  </Link>

                  {/* Dashboard and Logout Buttons - Right */}
                  <div className="flex items-center gap-2.5">
                    <Link
                      href="/profile?tab=learning-dashboard"
                      className={`px-3 bg-surface-4 py-2 cs-s-body-light transition-colors whitespace-nowrap !h-auto ${
                        pathname === '/profile' && searchParams.get('tab') === 'learning-dashboard'
                          ? ' text-text-default-strong font-medium'
                          : 'hover:bg-surface-3 text-text-default-medium'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Button
                      onClick={handleLogoutClick}
                      variant="ghost"
                      className="cs-s-body-light text-text-default-light hover:text-text-default-strong hover:bg-surface-1 whitespace-nowrap px-3 py-2 !h-auto"
                    >
                      Log out
                    </Button>
                  </div>
                </div>
              ) : (
                /* Login Button - when not logged in */
                (() => {
                  const loginLink = utilityLinks.find((link: any) => isLoginLink(link));
                  if (!loginLink) return null;
                  
                  return (
                    <div className="px-4 py-4 bg-surface-3 w-full flex justify-center">
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          loginModals?.openLoginModal();
                          setIsMobileMenuOpen(false);
                        }}
                        variant="PrimaryFillButton"
                        className="w-full h-10 gap-2 pt-4 pr-3 pb-4 pl-3 flex items-center justify-center bg-[#AC75FF] hover:opacity-90 transition-opacity"
                      >
                        <span className="cs-button">{loginLink?.menu_link?.title || 'Login'}</span>
                      </Button>
                    </div>
                  );
                })()
              )}
              

              {/* Header Navigation Links - Catalog, Learning Paths, Training Instance */}
              <div className="pt-2" />
              {NAVIGATION_LINKS.map((link, index) => {
                // Only show auth-required links if user is logged in
                if (link.requiresAuth && !isLoggedIn) return null;
                
                // Check if link is active
                const isActive = (() => {
                  if (link.href.includes('?')) {
                    // For links with query params
                    const [path, query] = link.href.split('?');
                    const tabParam = query.split('=')[1];
                    return pathname === path && searchParams.get('tab') === tabParam;
                  } else {
                    // For simple paths
                    return pathname === link.href || pathname.startsWith(link.href + '/');
                  }
                })();
                
                return (
                  <Link
                    key={`${link.label}-${index}`}
                    href={link.href}
                    target={link.openInNewTab ? '_blank' : '_self'}
                    rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                    className={`px-4 py-2 cs-s-body transition-colors whitespace-nowrap ${
                      isActive 
                        ? 'bg-surface-3 text-text-default-strong font-medium' 
                        : 'hover:bg-surface-3 text-text-default-medium'
                    }`}
                    data-test-id={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    onClick={(e) => {
                      handleCatalogNavClick(e, link);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="border-t border-border-medium my-2" />

              {/* Explore by product - Mobile only */}
              <button
                onClick={() => {
                  setShowProductMenu(true);
                }}
                className="px-4 py-2 cs-s-body transition-colors whitespace-nowrap text-left hover:bg-surface-3 text-text-default-medium flex items-center justify-between w-full"
              >
                <span>Explore by product</span>
                <MaterialIcons iconName="chevron_right" className="!text-[20px] text-text-default-soft" />
              </button>

              {/* Profile section links - only when user is inside profile (mobile view) */}
              {(pathname === '/profile' || pathname.startsWith('/profile/')) && isLoggedIn && (
                <>
                  <div className="border-t border-border-medium my-2" />
                  {PROFILE_SECTION_NAV_ITEMS.map((item) => {
                    const isProfileSectionActive = pathname === '/profile' && searchParams.get('tab') === item.section;
                    return (
                      <Link
                        key={item.section}
                        href={`/profile?tab=${item.section}`}
                        className={`px-4 py-2 cs-s-body transition-colors whitespace-nowrap block ${
                          isProfileSectionActive
                            ? 'bg-surface-3 text-text-default-strong font-medium'
                            : 'hover:bg-surface-3 text-text-default-medium'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </>
              )}

              {/* Divider */}
              <div className="border-b border-border-medium my-2" />

              {/* Utility Links from Announcement Bar (excluding login/logout) */}
              {utilityLinks.filter((link: any) => !isLoginLink(link) && !link?.isLogout).map((link: any, index: number) => {
                const isContentstackLink = link?.isContentstackLink;
                
                return (
                  <a
                    key={link?._metadata?.uid || index}
                    href={link?.menu_link?.href || '#'}
                    target={link?.open_in_new_tab ? '_blank' : '_self'}
                    rel={link?.open_in_new_tab ? 'noopener noreferrer' : undefined}
                    className="px-4 py-2 cs-s-body-light transition-colors whitespace-nowrap hover:bg-surface-3"
                    data-test-id="utility-nav-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link?.menu_link?.title}
                  </a>
                );
              })}

              {/* Theme Settings - at the last */}
              <div
                onClick={() => {
                  if (setTheme) {
                    if (theme === 'light') {
                      setTheme('dark');
                    } else if (theme === 'dark') {
                      setTheme('light');
                    } else {
                      setTheme('dark');
                    }
                  }
                }}
                className="px-4 py-2 w-full flex items-center justify-between hover:bg-surface-3 transition-colors cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label="Toggle theme"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (setTheme) {
                      if (theme === 'light') {
                        setTheme('dark');
                      } else if (theme === 'dark') {
                        setTheme('light');
                      } else {
                        setTheme('dark');
                      }
                    }
                  }
                }}
              >
                <span className="cs-s-body">Theme Settings</span>
                <ThemeToggle />
              </div>
            </div>
          </div>
      </div>

      {/* Logout confirmation modal is shared app-wide — see LogoutConfirmationController in app/layout.tsx */}

      {/* Product Slide Menu */}
      <ProductSlideMenu
        isOpen={showProductMenu}
        onClose={() => setShowProductMenu(false)}
        products={products}
        headerHeight={headerHeight}
        isLoading={isLoadingProducts}
        onProductClick={() => setIsMobileMenuOpen(false)}
        dashboardSection={
          isLoggedIn ? (
            <div className="px-4 py-4 bg-surface-3 w-full flex items-center justify-between">
              {/* Profile Icon - Left */}
              <Link
                href="/profile"
                className="flex items-center justify-center transition-opacity hover:opacity-80 flex-shrink-0"
                aria-label="Profile"
                data-test-id="profile-avatar"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setShowProductMenu(false);
                }}
              >
                <ProfileAvatar
                  profilePicture={profilePicture}
                  userInitials={userInitials}
                  cacheBust={profilePictureCacheBust}
                  size="sm"
                />
              </Link>

              {/* Dashboard and Logout Buttons - Right */}
              <div className="flex items-center gap-2.5">
                <Link
                  href="/profile?tab=learning-dashboard"
                  className={`px-3 bg-surface-4 py-2 cs-s-body-light transition-colors whitespace-nowrap !h-auto ${
                    pathname === '/profile' && searchParams.get('tab') === 'learning-dashboard'
                      ? ' text-text-default-strong font-medium'
                      : 'hover:bg-surface-3 text-text-default-medium'
                  }`}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowProductMenu(false);
                  }}
                >
                  Dashboard
                </Link>
                <Button
                  onClick={handleLogoutClick}
                  variant="ghost"
                  className="cs-s-body-light text-text-default-light hover:text-text-default-strong hover:bg-surface-1 whitespace-nowrap px-3 py-2 !h-auto"
                >
                  Log out
                </Button>
              </div>
            </div>
          ) : (
            /* Login Button - when not logged in */
            (() => {
              const loginLink = utilityLinks.find((link: any) => isLoginLink(link));
              if (!loginLink) return null;
              
              return (
                <div className="px-4 py-4 bg-surface-3 w-full flex justify-center">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      loginModals?.openLoginModal();
                      setIsMobileMenuOpen(false);
                      setShowProductMenu(false);
                    }}
                    variant="PrimaryFillButton"
                    className="w-full h-10 gap-2 pt-4 pr-3 pb-4 pl-3 flex items-center justify-center bg-[#AC75FF] hover:opacity-90 transition-opacity"
                  >
                    <span className="cs-button">{loginLink?.menu_link?.title || 'Login'}</span>
                  </Button>
                </div>
              );
            })()
          )
        }
      />
    </div>
    </>
  );
}

